import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LoginCredentials, PublicUser } from "@snipe/core";
import { ApiError, request } from "@/lib/api";
import { keys } from "@/lib/queries";
import { LoginResponse, NoContent, UserResponse } from "@/lib/schemas";

/**
 * Who is signed in, according to the server and nothing else.
 *
 * There is no client-side session state to speak of: the credential is an
 * httpOnly cookie this code cannot read, so "am I signed in" can only ever be
 * answered by asking `/auth/me`. That is a feature -- the answer includes the
 * CURRENT role, so an operator demoted a minute ago stops seeing /ops on the
 * next refetch rather than at the end of a cached hour.
 */
export interface SessionValue {
  user: PublicUser | null;
  loading: boolean;
  signIn: (credentials: LoginCredentials) => Promise<void>;
  signOut: () => Promise<void>;
  signInError: ApiError | null;
  signingIn: boolean;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const client = useQueryClient();

  const me = useQuery({
    queryKey: keys.me,
    queryFn: async (): Promise<PublicUser | null> => {
      try {
        return (await request("/auth/me", UserResponse)).user;
      } catch (error) {
        // 401 is the ordinary answer for a visitor, not a failure to retry.
        if (error instanceof ApiError && error.unauthenticated) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  const signInMutation = useMutation({
    mutationFn: async (credentials: LoginCredentials) =>
      (await request("/auth/login", LoginResponse, { method: "POST", body: credentials })).user,
    onSuccess: (user) => {
      client.setQueryData(keys.me, user);
    },
  });

  const signOutMutation = useMutation({
    mutationFn: () => request("/auth/logout", NoContent, { method: "POST" }),
    onSettled: () => {
      // Cleared even when the call failed: whatever the server thinks, this
      // browser must stop showing one account's data to the next person.
      client.setQueryData(keys.me, null);
      client.removeQueries({ queryKey: keys.summary });
      client.removeQueries({ queryKey: keys.identities });
      client.removeQueries({ queryKey: keys.alerts });
    },
  });

  const value = useMemo<SessionValue>(
    () => ({
      user: me.data ?? null,
      loading: me.isPending,
      signIn: async (credentials) => {
        await signInMutation.mutateAsync(credentials);
      },
      signOut: async () => {
        await signOutMutation.mutateAsync();
      },
      signInError: signInMutation.error instanceof ApiError ? signInMutation.error : null,
      signingIn: signInMutation.isPending,
    }),
    [me.data, me.isPending, signInMutation, signOutMutation],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error("useSession used outside a SessionProvider");
  return value;
}
